import { SHARE_URL } from "./shareCard";

/**
 * The daily challenge's pure core (GRA-139): which ten Rounds a given day
 * plays, and how a finished run reads as text. No stores, no storage, no
 * clock beyond the one `Date` handed in, so every determinism claim in
 * ADR-0009 is checkable here.
 *
 * The site is fully static, so "everyone gets the same ten" has to come from
 * the date alone: there is no server to hand out a puzzle.
 */

/** How many Rounds a daily challenge run plays. */
export const CHALLENGE_LENGTH = 10;

/**
 * The day a run belongs to, in the player's own timezone (`YYYY-MM-DD`).
 *
 * Local, not UTC: a player's "today" is the date on their wall, and a
 * challenge that rolled over at 01:00 local (or at 16:00, west of UTC) would
 * read as broken. The cost is that two players in different zones are on
 * different puzzles for part of the day, which nobody can see without
 * comparing pastes, whereas a mistimed rollover is visible immediately.
 */
export function localDateKey(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * FNV-1a, 32-bit. Chosen for being short enough to read and verify in one
 * sitting: the requirement here is a well-mixed number from a string, not
 * cryptographic strength.
 */
export function hashString(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    // The FNV prime (16777619) via shifts, so the product stays in 32 bits.
    hash = (hash + (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)) >>> 0;
  }
  return hash >>> 0;
}

/** Namespace the hash so a Round id's score is specific to this game and day. */
function roundScore(dateKey: string, id: string): number {
  return hashString(`candlealpha:challenge:${dateKey}:${id}`);
}

/** What the picker needs of a Round: a stable id. */
type Identified = { id: string };

/**
 * The day's Rounds, in play order, picked from `bank` by hashing each Round's
 * own id together with the date and taking the lowest scores.
 *
 * Hashing per id rather than seeding one PRNG and indexing into the bank is
 * what makes the pick survive a bank update as well as it can: a Round's
 * score depends on nothing but its id and the date, so adding or removing
 * Rounds cannot reshuffle the ones it did not touch. Adding N Rounds to a
 * bank of B only changes the day's ten when one of them out-scores the
 * current tenth (roughly a 10N/B chance); removing a picked Round swaps in
 * the eleventh and leaves the other nine alone. Seeding a PRNG on the date
 * and drawing indices would instead have changed all ten for everybody the
 * moment the bank's length changed.
 *
 * Ties break on the id, so the order never depends on `bank`'s own ordering
 * (shard order, filter order, the order the pipeline happened to emit).
 */
export function pickChallengeRounds<R extends Identified>(
  bank: R[],
  dateKey: string,
  count: number = CHALLENGE_LENGTH,
): R[] {
  return bank
    .map((round) => ({ round, score: roundScore(dateKey, round.id) }))
    .sort((a, b) =>
      a.score !== b.score
        ? a.score - b.score
        : a.round.id < b.round.id
          ? -1
          : a.round.id > b.round.id
            ? 1
            : 0,
    )
    .slice(0, Math.max(0, count))
    .map((entry) => entry.round);
}

/**
 * The Rounds a run actually serves, and the ids it pins (GRA-145).
 *
 * `pickChallengeRounds` answers "which ten does this date deal from this
 * pool", which is only the same question on the day a run starts. Once a run
 * exists it has *pinned* ids, and those win: a bank redeployed mid-run must
 * not move the Rounds a player has left. So a pinned id is looked up in the
 * current pool and served, and only a slot whose Round has genuinely gone
 * missing is refilled.
 *
 * The refill is the day's own ranking, skipping every id already spoken for,
 * so it is deterministic (two players who lost the same Round get the same
 * replacement) and local (no other slot moves). A slot that cannot be filled
 * at all, from a pool smaller than the run, simply shortens the run.
 */
export function resolveChallengeOrder<R extends Identified>(
  pool: R[],
  dateKey: string,
  pinnedIds: readonly string[] | null | undefined,
  count: number = CHALLENGE_LENGTH,
): { rounds: R[]; ids: string[] } {
  const ranked = pickChallengeRounds(pool, dateKey, pool.length);
  const size = Math.max(0, count);

  if (!pinnedIds || pinnedIds.length === 0) {
    const rounds = ranked.slice(0, size);
    return { rounds, ids: rounds.map((round) => round.id) };
  }

  const byId = new Map(pool.map((round) => [round.id, round] as const));
  const slots = pinnedIds.slice(0, size).map((id) => byId.get(id) ?? null);
  const taken = new Set(slots.filter((round): round is R => round !== null).map((r) => r.id));
  const spares = ranked.filter((round) => !taken.has(round.id));

  let spare = 0;
  const rounds: R[] = [];
  for (const slot of slots) {
    if (slot) {
      rounds.push(slot);
      continue;
    }
    const fill = spares[spare];
    spare += 1;
    if (fill) rounds.push(fill);
  }
  return { rounds, ids: rounds.map((round) => round.id) };
}

/** The day's Round ids, in play order. */
export function pickChallengeIds(
  ids: string[],
  dateKey: string,
  count: number = CHALLENGE_LENGTH,
): string[] {
  return pickChallengeRounds(
    ids.map((id) => ({ id })),
    dateKey,
    count,
  ).map((entry) => entry.id);
}

/**
 * The `?date=YYYY-MM-DD` override, for reproducing a specific day's ten
 * Rounds during development and browser automation. Mirrors `?round=` in
 * lib/rounds.ts: gated on `import.meta.env.DEV`, so it is inert in a
 * production build whatever the URL says.
 */
export function pinnedChallengeDate(): string | null {
  if (!import.meta.env.DEV) return null;
  if (typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get("date");
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

/** Today's challenge date: the dev override if pinned, else the local date. */
export function challengeDate(now: Date = new Date()): string {
  return pinnedChallengeDate() ?? localDateKey(now);
}

const MARK = { correct: "🟩", wrong: "🟥" } as const;

/**
 * The copyable result (spec §16's share card, one run rather than a session):
 * the day, the score, and a square per Round in play order, so a paste says
 * how the run went without spoiling which way any Round resolved.
 *
 * `betterThan` appends the day's Standing (GRA-142) to the score line, and
 * only when the server actually offered a percentile: a paste that silently
 * omitted it would read as a worse score, so an absent number is left absent
 * rather than turned into a 0%.
 */
export function buildChallengeShareText(opts: {
  date: string;
  outcomes: boolean[];
  practice?: boolean;
  betterThan?: number | null;
}): string {
  const correct = opts.outcomes.filter(Boolean).length;
  const squares = opts.outcomes.map((ok) => (ok ? MARK.correct : MARK.wrong)).join("");
  const heading = `candlealpha daily ${opts.date}${opts.practice ? " (practice)" : ""}`;
  const standing =
    typeof opts.betterThan === "number" ? ` · better than ${opts.betterThan}%` : "";
  return `${heading}\n${correct}/${opts.outcomes.length} ${squares}${standing}\n${SHARE_URL}`;
}
